import { defaultMessages, minLength } from "utils/validator";

import { FieldError } from "../../error";
import { Field } from "../../field";
import { Merchant } from "../../merchant";
import { CountryList, MerchantField } from "../../types";
import { countryPhoneMap } from "../../utils/countryPhoneMap";
import { maxLength, validatePhoneFromCountryFormat } from "../../validate";

const testValueByCountryMap: Record<CountryList, string> = {
  "United Kingdom": "+441234567900",
  "United States": "+14151234567",
  "New Zealand": "+641234567890",
  Canada: "+12041234567",
  Australia: "+61123456789",
};
export class PhoneField extends Field<string> {
  phoneCountryNameForValidation: string;

  constructor(model: Merchant, opts: Partial<MerchantField<string>> = {}) {
    super(model, {
      dataType: "string",
      testValue: "+12345678900",
      propertyName: "phone",
      uiFieldType: "Phone",
      uiInputBaseProps: {
        autoComplete: "tel",
      },
      applicable: true,
      shown: true,
      required: true,
      ...opts,
    });
    this.phoneCountryNameForValidation = "";
  }

  get testValue(): string {
    const registeredCountry = this._model.lead.registeredCountry.value;
    return registeredCountry
      ? testValueByCountryMap[registeredCountry]
      : "+12345678900";
  }

  _validate(): void {
    const value = this.value ?? "";

    if (!value) {
      return;
    }

    const valueToValidate = "" + this.value;
    const country = this.phoneCountryNameForValidation;
    const phoneFormat = countryPhoneMap[country]?.format;

    // New Zealand phone format is a range depending on if landline or mobile
    // landline is 8 digits (excl. +64) and mobile is 9-11 (excl. +64)
    // https://en.wikipedia.org/wiki/Telephone_numbers_in_New_Zealand#Present_numbering_plan
    if (country === "New Zealand") {
      if (!maxLength(13)(valueToValidate)) {
        throw new FieldError(this, `Max length 13`, "InvalidLength");
      }

      if (!minLength(10)(valueToValidate)) {
        throw new FieldError(this, `Min length 10`, "InvalidLength");
      }
      // validates phone number length based on country phone format if provided
    } else if (country && phoneFormat) {
      if (!validatePhoneFromCountryFormat(phoneFormat)(valueToValidate)) {
        throw new FieldError(
          this,
          defaultMessages.isValidPhoneNumber,
          "InvalidPhone"
        );
      }
    } else {
      // E.164 permits a maximum length of 15 digits for the complete international phone number consisting of the country code, the national routing code (area code), and the subscriber number.
      //en.wikipedia.org/wiki/Telephone_numbering_plan
      if (!maxLength(15)(valueToValidate)) {
        throw new FieldError(this, `Max length 15`, "InvalidLength");
      }
      // From 1 October 2013, all existing 4-digit telephone numbers on St Helena were to be prefixed by a '2' to move from a 4-digit to a 5-digit numbering plan
      // https://en.wikipedia.org/wiki/Telephone_numbers_in_Saint_Helena_and_Tristan_da_Cunha
      if (!minLength(5)(valueToValidate)) {
        throw new FieldError(this, `Min length 5`, "InvalidLength");
      }
    }
  }
}
